/**
 * Shared organization profile types for API contracts between frontend and backend
 */
import { z } from "zod";
// ===== ADDRESS VALIDATION SCHEMAS =====
const addressLine1Schema = z.string().trim().min(1).max(255);
const addressLine2Schema = z.string().trim().max(255);
const citySchema = z.string().trim().min(1).max(100);
const stateSchema = z.string().regex(/^[A-Z]{2}$/, "Must be 2-letter state code");
const zipCodeSchema = z
    .string()
    .regex(/^\d{5}(-\d{4})?$/, "Must be 12345 or 12345-6789");
// ===== SOCIAL MEDIA SCHEMA =====
export const SocialMediaSchema = z.object({
    facebook: z.string().trim().max(255).optional().nullable(),
    twitter: z.string().trim().max(255).optional().nullable(),
    instagram: z.string().trim().max(255).optional().nullable(),
    linkedin: z.string().trim().max(255).optional().nullable(),
    youtube: z.string().trim().max(255).optional().nullable(),
    tiktok: z.string().trim().max(255).optional().nullable(),
});
// ===== ADDRESS SCHEMAS =====
export const OrganizationAddressSchema = z.object({
    addressLine1: addressLine1Schema,
    addressLine2: addressLine2Schema.optional().nullable(),
    city: citySchema,
    state: stateSchema,
    zipCode: zipCodeSchema,
});
export const OrganizationOptionalAddressSchema = z.object({
    addressLine1: addressLine1Schema.optional().nullable(),
    addressLine2: addressLine2Schema.optional().nullable(),
    city: citySchema.optional().nullable(),
    state: stateSchema.optional().nullable(),
    zipCode: zipCodeSchema.optional().nullable(),
});
// ===== ORGANIZATION PROFILE RESPONSE =====
export const OrganizationProfileResponseSchema = z.object({
    businessLegalName: z.string().nullable(),
    dba: z.string().nullable(),
    ein: z.string().nullable(),
    phone: z.string().nullable(),
    contactName: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    primaryAddress: OrganizationOptionalAddressSchema.nullable(),
    mailingAddress: OrganizationOptionalAddressSchema.nullable(),
    mailingSameAsPrimary: z.boolean(),
    billingAddress: OrganizationOptionalAddressSchema.nullable(),
    billingSameAsPrimary: z.boolean(),
    logoFileId: z.string().uuid().nullable(),
    logoUrl: z.string().url().nullable(),
    iconFileId: z.string().uuid().nullable(),
    iconUrl: z.string().url().nullable(),
    socialMedia: SocialMediaSchema.nullable(),
});
// ===== UPDATE ORGANIZATION PROFILE INPUT =====
export const UpdateOrganizationProfileInputSchema = z.object({
    // Business Identity
    businessLegalName: z.string().trim().max(255).optional().nullable(),
    dba: z.string().trim().max(255).optional().nullable(),
    ein: z
        .string()
        .regex(/^\d{2}-\d{7}$/, "Must be in format XX-XXXXXXX")
        .optional()
        .nullable(),
    // Contact Information
    phone: z
        .string()
        .regex(/^\+?[\d\s\-().]+$/, "Invalid phone number format")
        .optional()
        .nullable(),
    contactName: z.string().trim().max(255).optional().nullable(),
    email: z.string().email().optional().nullable(),
    website: z
        .string()
        .trim()
        .max(255)
        .regex(/^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/\S*)?$/i, "Must be a valid URL (e.g., https://www.example.com)")
        .optional()
        .nullable(),
    // Primary Address
    primaryAddressLine1: addressLine1Schema.optional().nullable(),
    primaryAddressLine2: addressLine2Schema.optional().nullable(),
    primaryCity: citySchema.optional().nullable(),
    primaryState: stateSchema.optional().nullable(),
    primaryZipCode: zipCodeSchema.optional().nullable(),
    // Mailing Address
    mailingAddressLine1: addressLine1Schema.optional().nullable(),
    mailingAddressLine2: addressLine2Schema.optional().nullable(),
    mailingCity: citySchema.optional().nullable(),
    mailingState: stateSchema.optional().nullable(),
    mailingZipCode: zipCodeSchema.optional().nullable(),
    mailingSameAsPrimary: z.boolean().optional(),
    // Billing Address
    billingAddressLine1: addressLine1Schema.optional().nullable(),
    billingAddressLine2: addressLine2Schema.optional().nullable(),
    billingCity: citySchema.optional().nullable(),
    billingState: stateSchema.optional().nullable(),
    billingZipCode: zipCodeSchema.optional().nullable(),
    billingSameAsPrimary: z.boolean().optional(),
    // Branding
    logoFileId: z.string().uuid().optional().nullable(),
    iconFileId: z.string().uuid().optional().nullable(),
    // Social Media
    socialMedia: SocialMediaSchema.optional().nullable(),
});
